import {
  documentQuery,
  evaluationReportQuery,
  useAcceptAdEvalReportMutation,
  useFinalizeEvaluationFieldMutation,
  useUnfinalizeEvaluationFieldMutation,
  useUpdateEvaluationFieldMutation,
} from "@common/api"
import { Form } from "@common/components/ui/form"
import { useSharedForm } from "@common/hooks/use-shared-form"
import { handleError } from "@common/utils"
import { zodResolver } from "@hookform/resolvers/zod"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, notFound, useNavigate } from "@tanstack/react-router"
import { useState } from "react"
import useCustomToast from "@/hooks/use-custom-toast"
import { AcceptAssessmentBar } from "../-components/accept-assessment-bar"
import {
  AssessmentDocumentPanel,
  AssessmentPanels,
  type ViewerFocus,
} from "../-components/assessment-panels"
import { SectionCard } from "../-components/section-card"
import {
  type AdAssessmentInput,
  adAssessmentSchema,
  mapAdReportToForm,
} from "../-lib/schema"
import { type AdFieldName, resolveAdSections } from "../-lib/sections"

export const Route = createFileRoute("/_layout/assessments/ad/$reportId")({
  loader: async ({ context: { queryClient }, params: { reportId } }) => {
    const report = await queryClient.ensureQueryData(
      evaluationReportQuery({ id: reportId }),
    )
    if (!report.document_id) throw notFound()
    const document = await queryClient.ensureQueryData(
      documentQuery({ document_id: report.document_id }),
    )
    return { breadcrumb: `AD assessment: ${document.file_name}` }
  },
  component: AdAssessmentPage,
})

function AdAssessmentPage(): React.ReactElement | null {
  const { reportId } = Route.useParams()
  const navigate = useNavigate()
  const showToast = useCustomToast()
  const { data: report } = useSuspenseQuery(
    evaluationReportQuery({ id: reportId }),
  )
  const [focus, setFocus] = useState<ViewerFocus | null>(null)

  const form = useSharedForm<AdAssessmentInput>({
    resolver: zodResolver(adAssessmentSchema),
    values: mapAdReportToForm(report),
    resetOptions: { keepDirtyValues: true },
    mode: "onBlur",
    criteriaMode: "all",
  })

  const updateField = useUpdateEvaluationFieldMutation()
  const finalizeField = useFinalizeEvaluationFieldMutation()
  const unfinalizeField = useUnfinalizeEvaluationFieldMutation()
  const acceptReport = useAcceptAdEvalReportMutation()

  const documentId = report.document_id
  if (!documentId) return null

  const sections = resolveAdSections(report)
  const finalisedCount = sections.filter((section) => section.finalised).length
  const acceptSection = async (name: AdFieldName) => {
    if (!(await form.trigger(name))) return
    const persisted = form.getFieldState(name).isDirty
      ? updateField.mutateAsync({
          report_id: reportId,
          field_name: name,
          fieldUpdateRequest: { edited_value: form.getValues(name) },
        })
      : Promise.resolve(null)
    return persisted
      .then(() =>
        finalizeField.mutateAsync({ report_id: reportId, field_name: name }),
      )
      .catch((error) => handleError(error, showToast))
  }

  const reopenSection = (name: AdFieldName) =>
    unfinalizeField.mutate(
      { report_id: reportId, field_name: name },
      { onError: (error) => handleError(error, showToast) },
    )

  const acceptAssessment = () =>
    acceptReport.mutate(
      { acceptedAdAssessmentCreate: { trace_ad_eval_report_id: reportId } },
      {
        onSuccess: (accepted) => {
          showToast("Success", "Assessment accepted", "success")
          navigate({
            to: "/aircraft/$aircraftId",
            params: { aircraftId: String(accepted.aircraft_id) },
          })
        },
        onError: (error) => handleError(error, showToast),
      },
    )

  return (
    <Form {...form}>
      <AssessmentPanels
        viewer={
          <AssessmentDocumentPanel
            focus={focus}
            subjectDocumentId={documentId}
          />
        }
        form={
          <>
            <div className="flex flex-col gap-3 p-4">
              {sections.map((section) => (
                <SectionCard<AdAssessmentInput>
                  key={section.name}
                  section={section}
                  dirty={!!form.formState.dirtyFields[section.name]}
                  subjectDocumentId={documentId}
                  onAccept={() => acceptSection(section.name)}
                  onReopen={() => reopenSection(section.name)}
                  onShowReference={(reference) =>
                    setFocus({
                      documentId: reference.document_id,
                      page: reference.page,
                      evidence: [reference.text],
                    })
                  }
                />
              ))}
            </div>
            <AcceptAssessmentBar
              finalisedCount={finalisedCount}
              total={sections.length}
              hasAircraft={report.aircraft_id != null}
              pending={acceptReport.isPending}
              onAccept={acceptAssessment}
            />
          </>
        }
      />
    </Form>
  )
}
